import { useEffect, useState } from 'react'
import type { Mode } from '../../lib/types'

const CHIPS: { mode: Mode; label: string; icon: string }[] = [
  { mode: 'drive', label: 'Drive', icon: '🚗' },
  // UI says "Train"; the stored enum value is 'rail'.
  { mode: 'rail', label: 'Train', icon: '🚆' },
  { mode: 'fly', label: 'Fly', icon: '✈️' },
]

const IATA = /^[A-Z]{3}$/

export interface ParsedAirports {
  airports: string[]
  skipped: string[]
}

export function parseAirports(raw: string): ParsedAirports {
  const tokens = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)

  const airports: string[] = []
  const skipped: string[] = []
  for (const token of tokens) {
    const upper = token.toUpperCase()
    if (IATA.test(upper)) {
      if (!airports.includes(upper)) airports.push(upper)
    } else {
      skipped.push(token)
    }
  }
  return { airports, skipped }
}

export interface ModesPickerProps {
  /** Persisted household.modes_ok. Empty array is a legitimate saved value. */
  modes: Mode[]
  /** Persisted household.nearest_airports (IATA codes). */
  airports: string[]
  disabled?: boolean
  onModesChange: (modes: Mode[]) => void
  onAirportsChange: (airports: string[]) => void
}

export function ModesPicker({
  modes,
  airports,
  disabled = false,
  onModesChange,
  onAirportsChange,
}: ModesPickerProps) {
  const [raw, setRaw] = useState(airports.join(', '))
  const [skipped, setSkipped] = useState<string[]>([])

  useEffect(() => {
    setRaw(airports.join(', '))
  }, [airports])

  const flying = modes.includes('fly')

  function toggle(mode: Mode) {
    if (disabled) return
    const next = modes.includes(mode) ? modes.filter((m) => m !== mode) : [...modes, mode]
    // Saved as-is. No default is invented; [] is allowed.
    onModesChange(next)
  }

  function commitAirports() {
    const parsed = parseAirports(raw)
    setSkipped(parsed.skipped)
    // Toggling Fly off later hides this input but never erases what was saved.
    onAirportsChange(parsed.airports)
  }

  return (
    <div>
      <div className="spz-chips" role="group" aria-label="Acceptable travel modes">
        {CHIPS.map(({ mode, label, icon }) => {
          const on = modes.includes(mode)
          return (
            <button
              key={mode}
              type="button"
              className={`spz-chip${on ? ' spz-chip-on' : ''}`}
              aria-pressed={on}
              disabled={disabled}
              onClick={() => toggle(mode)}
            >
              <span aria-hidden="true">{icon}</span> {label}
            </button>
          )
        })}
      </div>

      {flying && (
        <label className="spz-field spz-airports">
          <span className="spz-label">Nearest airports</span>
          <input
            type="text"
            className="spz-input"
            placeholder="IAD, DCA"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            value={raw}
            disabled={disabled}
            onChange={(e) => setRaw(e.target.value)}
            onBlur={commitAirports}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                e.currentTarget.blur()
              }
            }}
          />
          <span className="spz-hint">Comma-separated 3-letter codes.</span>
          {skipped.length > 0 && (
            <span className="spz-warn" role="status">
              skipped: {skipped.map((s) => `'${s}'`).join(', ')} — use 3-letter codes like IAD
            </span>
          )}
        </label>
      )}

      {!flying && airports.length > 0 && (
        <p className="spz-hint">
          Airports on file ({airports.join(', ')}) are kept in case you turn Fly back on.
        </p>
      )}
    </div>
  )
}

export default ModesPicker
